/**
 * diagramNodeIcon - the logo (or initials badge) a System Architecture box shows, matching the
 * Systems Landscape logo cards.
 *
 * The box only knows its owning system's name (e.g. "NetSuite OneWorld"), so the logo comes from
 * the shared name-to-logo matching. Only the `system` name is matched, never the label: a label
 * like "Salesforce-NetSuite Middleware" names two products and would borrow the wrong logo.
 * - Any box whose system has a bundled logo shows it.
 * - A `system` box without one shows initials, so every system box carries an icon.
 * - Other boxes (people, process steps, data stores) without a logo show nothing.
 * Workflow diagrams get no icons: their steps all sit in one or two systems.
 */

import type { BprDiagramKind, BprDiagramNode } from '@protoss/shared';
import { landscapeInitials, landscapeLogo, landscapeLogoKeyForName } from '@protoss/shared';

export type DiagramNodeIcon = { logo: string; initials: null } | { logo: null; initials: string };

export function diagramNodeIcon(
  node: Pick<BprDiagramNode, 'type' | 'system'>,
  kind: BprDiagramKind
): DiagramNodeIcon | null {
  if (kind !== 'system-architecture') return null;
  const system = node.system.trim();
  if (!system) return null;
  const logo = landscapeLogo(landscapeLogoKeyForName(system));
  if (logo) return { logo, initials: null };
  return node.type === 'system' ? { logo: null, initials: landscapeInitials(system) } : null;
}
